// === SAA:WONINGSPECIFICATIES ===
// Single source of truth for the "Woningspecificaties" (building-spec) form, shared by the
// Woonhuis (opstal) and Inboedel (contents) product screens. Presentation-only / controlled:
// it renders the fields, options, required markers, locking and error display, and routes every
// change through `onFieldChange` (each screen's existing handleCustomFieldChange, which owns the
// setData / BFF mapping / side-effects). It performs NO validation, NO setData, NO BFF mapping and
// renders NO warnings — those stay in the screens.
//
// The only product difference is the area field: Inboedel has ONE (BA_OPPWON); Woonhuis has TWO
// (BA_BGM2 + BA_TOPPBGB). Everything else is shared and rendered in one canonical order.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { houseTypeOptionsFor } from '../utils/houseType';

type Variant = 'woonhuis' | 'inboedel';
type StyleMap = Record<string, React.CSSProperties>;

export interface WoningSpecificatiesProps {
  variant: Variant;
  ba: any; // data.Contractdocument.PP.BA
  styles: StyleMap; // the screen's own styles object (woonhuisStyles / inboedelStyles — same shape)
  onFieldChange: (fieldPath: string, value: any) => void; // = screen's handleCustomFieldChange (clears its own error)
  lockedFields: Set<string>; // = infofolioFilledFields
  errors: Record<string, string>; // = validationErrors
  onNumberBlur?: (fieldPath: string, raw: string) => void; // screen keeps numeric range validation
}

const BA = (code: string) => `Contractdocument.PP.BA.${code}`;
const OPT = 'screens.woonhuis.options';

// Option lists — single source. [code, i18nKeySuffix] under the given i18n base.
// Soort woning lives in utils/houseType (shared with the summary and the Woonhuis Infofolio
// mapper); the inboedel-only codes are filtered there per variant.
// Most-chosen subsets shown first in the long selects; "Anders (alle opties tonen)" reveals the
// full list. Codes only; the full lists above stay the source of labels/order.
const HOUSE_TYPE_COMMON = ['1', '2', 'T', 'H', 'U', 'A', 'L'];
const WALL_TYPE_COMMON = ['T', 'Y', 'U', 'M', 'H', 'S'];
const ROOF_TYPE_COMMON = ['P', 'H', 'N', 'J', 'R', 'L'];
const ADJACENT_BUILDING_COMMON = ['GW', 'KA', 'WI', 'P', 'AG', 'DA'];
const PROPERTY_USE_COMMON = ['P', 'A', 'D', 'H'];
const SHOW_ALL = '__show_all__';
// Wall types: already filtered (33) + alphabetically sorted by Dutch label (single source).
const WALL_TYPE: Array<[string, string]> = [
  ['X', 'aluminium'], ['S', 'mostlyConcrete'], ['O', 'corrugatedOnStoneBase'], ['D01', 'corrugatedSheets'],
  ['A', 'woodPlastic'], ['4', 'woodAluminium'], ['5', 'woodGlass'], ['P', 'woodStone'], ['9', 'metalPlastic'],
  ['C', 'stoneMetal'], ['6', 'steelStructure'], ['J', 'finnishWood'], ['F', 'glassOnConcrete'], ['G', 'glassOnMetal'],
  ['G01', 'glassFacade'], ['G03', 'glassFacadeCorrugated'], ['G02', 'glassFacadeStone'], ['H', 'mostlyWood'],
  ['N', 'woodOnStoneBase'], ['M', 'woodFrame'], ['K', 'woodFrameWoodExterior'], ['L', 'woodFrameStoneExterior'],
  ['D', 'plastic'], ['KR', 'syntheticReed'], ['B', 'metal'], ['3', 'pvcPolyester'], ['S01', 'sedumPlants'],
  ['E', 'steel'], ['W', 'steelSandwich'], ['T', 'mostlyStone'], ['Y', 'fullStone'], ['Q', 'stoneInSteel'],
  ['U', 'stoneWithWood'],
];
const ROOF_TYPE: Array<[string, string]> = [
  ['P', 'tiles'], ['J', 'concrete'], ['H', 'hard'], ['A', 'metal'], ['B', 'steel'], ['1', 'aluminium'],
  ['2', 'corrugatedSheets'], ['3', 'woodBitumen'], ['4', 'metalBitumen'], ['5', 'ruberoid'], ['6', 'tilesAsbestos'],
  ['7', 'tilesSteel'], ['8', 'tilesPlastic'], ['9', 'hardSedum'], ['10', 'plasticSheets'], ['11', 'wiredGlass'],
  ['12', 'screwReed'], ['13', 'nailReed'], ['14', 'ventilatedReed'], ['2A', 'corrugatedPlastic'], ['2B', 'corrugatedSteel'],
  ['C', 'wood'], ['D', 'partlyHardPartlyReed'], ['E', 'polyester'], ['F', 'canvas'], ['G', 'corrugatedPanels'],
  ['I', 'zinc'], ['K', 'syntheticReed'], ['L', 'slate'], ['M', 'mastic'], ['N', 'bitumen'], ['O', 'hardUnlined'],
  ['PW', 'sheetMetal'], ['Q', 'shingles'], ['Q1', 'shinglesBitumen'], ['Q2', 'shinglesWood'], ['R', 'reed'],
  ['R1', 'reedFireRetardant'], ['RI', 'reedImpregnated'], ['RU', 'rubber'], ['S', 'reedLinedTiles'],
  ['T', 'asbestosPanels'], ['U', 'straw'], ['V', 'steelSandwich'], ['W', 'partlyHardPartlyStraw'], ['X', 'metalPlastic'],
  ['Y', 'vegetation'], ['Z', 'other'],
];
// These use the code itself as the i18n key suffix.
const PROPERTY_USE_CODES = ['P', 'A', 'I', 'J', 'B', 'C', 'D', 'H'];
const FLOOR_CODES = ['G', 'H', 'J', 'S', 'Z'];
// ADNSBE belendend-gebouw list filtered to the 18 codes Rhion R515 accepts (the other 38 earn a
// "BA_BDRFBEL is niet acceptabel" rejection). Sorted by localized label at render time.
const ADJACENT_BUILDING_CODES = ['AG', 'BU', 'DA', 'GW', 'HK', 'JA', 'KA', 'KW', 'P', 'SA', 'VK', 'WC', 'WH', 'WI', 'WK', 'WM', 'WW', 'ZW'];
// Soort monument options: [AFD code, i18n key suffix under screens.woonhuis.monumentType].
const SRTMON_OPTIONS: Array<[string, string]> = [
  ['B', 'protected'], ['G', 'municipal'], ['P', 'provincial'],
  ['R', 'national'], ['Z', 'other'],
];

export function WoningSpecificaties({
  variant, ba, styles, onFieldChange, lockedFields, errors, onNumberBlur,
}: WoningSpecificatiesProps) {
  const { t, i18n } = useTranslation();
  // Selects whose full option list the user asked for (via "Anders"); a pick collapses it again.
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());
  // "Anders" re-opens the same select on the full list so the user picks straight away.
  const selectRefs = React.useRef<Record<string, HTMLSelectElement | null>>({});
  const [reopen, setReopen] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!reopen) return;
    setReopen(null);
    const el = selectRefs.current[reopen];
    if (!el) return;
    el.focus();
    // Still inside the click's user activation (Chromium 121+, Firefox 125+, Safari 17.4+);
    // elsewhere the focused select opens with Alt+Down.
    try { (el as HTMLSelectElement & { showPicker?: () => void }).showPicker?.(); } catch { /* no activation */ }
  }, [reopen]);
  const val = (code: string) => ba?.[code] ?? '';
  const isLocked = (lockKey: string, code: string) => lockedFields.has(lockKey) && !!ba?.[code];
  const errKey = (code: string) => errors[BA(code)];
  const placeholder = t('screens.woonhuis.placeholders.select');

  const labelEl = (labelKey: string, required: boolean) => (
    <label style={styles.fieldLabel}>
      {t(labelKey)} {required && <span style={styles.required}>*</span>}
    </label>
  );

  // ---- SELECT (i18n options) ----
  // `common` = most-chosen codes; the full list shows only while "Anders" is active. Collapsed,
  // the list is the common codes plus the current value (so a non-common pick stays visible).
  const renderSelect = (
    code: string, lockKey: string, labelKey: string, required: boolean,
    options: Array<{ value: string; label: string }>, common?: string[],
  ) => {
    const locked = isLocked(lockKey, code);
    const err = errKey(code);
    const current = val(code);
    const showAll = !common || expanded.has(code);
    const visible = showAll ? options : options.filter((o) => common.includes(o.value) || o.value === current);
    return (
      <div key={code} style={styles.addressField}>
        {labelEl(labelKey, required)}
        <select
          ref={(el) => { selectRefs.current[code] = el; }}
          value={current}
          disabled={locked}
          onChange={(e) => {
            if (e.target.value === SHOW_ALL) {
              setExpanded((prev) => new Set(prev).add(code));
              setReopen(code);
              return;
            }
            setExpanded((prev) => {
              if (!prev.has(code)) return prev;
              const next = new Set(prev);
              next.delete(code);
              return next;
            });
            onFieldChange(BA(code), e.target.value);
          }}
          className={err ? 'field-error' : ''}
          data-error={err ? 'true' : undefined}
          style={{ ...styles.fieldSelect, ...(err ? styles.fieldSelectError : {}) }}
        >
          <option value="">{placeholder}</option>
          {visible.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          {!showAll && <option value={SHOW_ALL}>{t('common.showAllOptions')}</option>}
        </select>
        {err && <p style={styles.errorMessage}>{err}</p>}
      </div>
    );
  };

  const optsFromPairs = (base: string, pairs: Array<[string, string]>, sorted = false) => {
    const list = pairs.map(([value, key]) => ({ value, label: t(`${base}.${key}`) }));
    if (sorted) list.sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    return list;
  };
  const optsFromCodes = (base: string, codes: string[], sorted = false) => {
    const list = codes.map((c) => ({ value: c, label: t(`${base}.${c}`) }));
    if (sorted) list.sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    return list;
  };

  // ---- NUMBER ----
  const renderNumber = (
    code: string, lockKey: string, labelKey: string, required: boolean,
    ph: string, opts?: { min?: number; max?: number; mb8?: boolean },
  ) => {
    const locked = isLocked(lockKey, code);
    const err = errKey(code);
    return (
      <div key={code} style={{ ...styles.addressField, ...(opts?.mb8 ? (styles.mb8 || {}) : {}) }}>
        {labelEl(labelKey, required)}
        <input
          type="number"
          min={opts?.min}
          max={opts?.max}
          value={val(code)}
          readOnly={locked}
          onChange={(e) => onFieldChange(BA(code), e.target.value === '' ? null : Number(e.target.value))}
          onBlur={(e) => { if (!locked) onNumberBlur?.(BA(code), e.target.value); }}
          className={err ? 'field-error' : ''}
          data-error={err ? 'true' : undefined}
          style={{ ...styles.fieldInput, ...(err ? styles.fieldInputError : {}), ...(locked ? styles.infofolioFilled : {}) }}
          placeholder={ph}
        />
        {err && <p style={styles.errorMessage}>{err}</p>}
      </div>
    );
  };

  // ---- RADIO (Ja/Nee) ----
  const renderRadio = (code: string, lockKey: string, labelKey: string, required: boolean) => {
    const locked = isLocked(lockKey, code);
    const err = errKey(code);
    const current = val(code);
    return (
      <div key={code} style={styles.addressField}>
        {labelEl(labelKey, required)}
        <div style={{ ...styles.radioGroup, ...(locked ? styles.radioGroupLocked : {}) }}>
          {(['J', 'N'] as const).map((opt) => {
            const selected = current === opt;
            const optErr = !selected && !!err;
            return (
              <div
                key={opt}
                style={selected ? styles.radioOptionSelected : (optErr ? styles.radioOptionError : styles.radioOption)}
                onClick={() => { if (locked) return; onFieldChange(BA(code), opt); }}
              >
                <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                  {selected && <div style={styles.radioDotInner} />}
                </div>
                {opt === 'J' ? t('common.yes') : t('common.no')}
              </div>
            );
          })}
        </div>
        {err && <p style={styles.errorMessage}>{err}</p>}
      </div>
    );
  };

  const filler = (key: string) => <div key={key} style={styles.addressField} aria-hidden="true" />;

  // ONE canonical field order for both variants. Variant-gated fields:
  //  - Monumentaal pand: Woonhuis only.
  //  - Oppervlakte: Inboedel = 1 field (BA_OPPWON); Woonhuis = 2 (BA_BGM2 + BA_TOPPBGB).
  // Both variants therefore have an EVEN field count, so the Eigen woning / Permanent bewoond
  // toggle pair shares a row. (Soort monument is pulled into its own row so it never shifts the
  // parity of the fields after it.)
  const items: React.ReactNode[] = [
    renderNumber('BA_BOUWJR', 'bouwjaar', 'screens.woonhuis.fields.constructionYear', true, t('screens.woonhuis.placeholders.constructionYear')),
    ...(variant === 'woonhuis' ? [renderRadio('BA_MONUMEN', 'monumen', 'screens.woonhuis.fields.monumentalProperty', true)] : []),
    renderSelect('BA_TYPEWON', 'typewon', 'screens.woonhuis.fields.houseType', true, optsFromPairs(`${OPT}.houseType`, houseTypeOptionsFor(variant), true), HOUSE_TYPE_COMMON),
    renderSelect('BA_AARDBW', 'aardbw', 'screens.woonhuis.fields.wallType', true, optsFromPairs(`${OPT}.wallType`, WALL_TYPE), WALL_TYPE_COMMON),
    renderSelect('BA_AARDDAK', 'aarddak', 'screens.woonhuis.fields.roofType', true, optsFromPairs(`${OPT}.roofType`, ROOF_TYPE), ROOF_TYPE_COMMON),
    ...(variant === 'woonhuis' ? [renderSelect('BA_AARDVLR', 'aardvlr', 'screens.woonhuis.fields.floorConstruction', false, optsFromCodes(`${OPT}.floorConstruction`, FLOOR_CODES))] : []),
    ...(variant === 'inboedel'
      ? [renderNumber('BA_OPPWON', 'oppwon', 'screens.inboedel.fields.surfaceArea', true, '100', { min: 1 })]
      : [
          renderNumber('BA_BGM2', 'bgm2', 'screens.woonhuis.fields.groundFloorArea', false, 'm2', { min: 0, mb8: true }),
          renderNumber('BA_TOPPBGB', 'toppbgb', 'screens.woonhuis.fields.totalAreaOutbuildings', false, 'm2', { min: 0, max: 100, mb8: true }),
        ]),
    // Both toggles always render (locked = disabled, like the fields above): an answer the
    // customer never sees is a declaration they never made. Infofolio answers Eigen woning
    // (koop/huur); Permanent bewoond it does not, so the hooks only prefill it, never lock it.
    renderRadio('BA_EIGENWO', 'eigenwo', 'screens.woonhuis.fields.ownHome', true),
    renderRadio('BA_PERMBEW', 'permbew', 'screens.woonhuis.fields.permanentResident', true),
    renderSelect('BA_WONING', 'woning', 'screens.woonhuis.fields.propertyUse', true, optsFromCodes(`${OPT}.propertyUse`, PROPERTY_USE_CODES), PROPERTY_USE_COMMON),
    renderSelect('BA_BDRFBEL', 'bdrfbel', 'screens.woonhuis.fields.adjacentBuilding', true, optsFromCodes(`${OPT}.adjacentBuilding`, ADJACENT_BUILDING_CODES, true), ADJACENT_BUILDING_COMMON),
  ];

  // Flow the fields into 2-column rows.
  const rows: React.ReactNode[][] = [];
  for (let i = 0; i < items.length; i += 2) rows.push(items.slice(i, i + 2));

  const showSrtmon = variant === 'woonhuis' && ba?.BA_MONUMEN === 'J';

  return (
    <>
      {rows.map((pair, idx) => (
        <React.Fragment key={`woningspec-row-${idx}`}>
          <div className="woonhuis-address-row">
            {pair}
            {pair.length === 1 && filler(`woningspec-filler-${idx}`)}
          </div>
          {/* Soort monument — own row right after the Bouwjaar/Monument row (Woonhuis, monument = Ja) */}
          {idx === 0 && showSrtmon && (
            <div className="woonhuis-address-row">
              {renderSelect('BA_SRTMON', 'srtmon', 'screens.woonhuis.fields.monumentType', true,
                SRTMON_OPTIONS.map(([value, key]) => ({ value, label: t(`screens.woonhuis.monumentType.${key}`) })))}
              {filler('woningspec-srtmon-filler')}
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

export default WoningSpecificaties;
